import { mainLogger as log } from "@/app/logger"
import { EventType } from "@/app/types/event_types"
import { getToken } from "@/lib/get_token"

export interface EventProps {
  event_ts_utc: string
  event_type: EventType
  event_data: Object
  event_env?: string
  session_id?: string
  user_id?: string
}

export class EventTracker {
  public eventBuffer: EventProps[] = []
  private processedEvents: Set<string> = new Set()
  private maxBufferSize: number
  private static instance: EventTracker | null = null
  private eventFlushInterval: NodeJS.Timeout | null = null

  constructor(maxBufferSize = 10) {
    this.maxBufferSize = maxBufferSize
    if (EventTracker.instance) {
      return EventTracker.instance
    }
    EventTracker.instance = this

    this.autoFlushEvents()
  }

  static get(): EventTracker {
    return EventTracker.instance ?? new EventTracker()
  }

  private generateEventKey(event: EventProps): string {
    return `${event.event_type}-${event.event_ts_utc}`
  }

  private resetEvents(): void {
    this.eventBuffer = []
  }

  public async trackEvent({
    event,
    data = {},
  }: {
    event: EventType
    data?: object
  }): Promise<void> {
    try {
      const eventProps: EventProps = {
        event_ts_utc: new Date().toUTCString(),
        event_type: event,
        event_data: data,
        session_id: await getToken(),
      }

      const eventKey = this.generateEventKey(eventProps)
      if (this.processedEvents.has(eventKey)) {
        log.warn("Duplicate event detected:", eventProps)
        return
      }

      log.debug("Tracking event:", eventProps)

      this.eventBuffer.push(eventProps)
      this.processedEvents.add(eventKey)

      if (this.eventBuffer.length >= this.maxBufferSize) {
        this.sendEvents()
      }
    } catch (error) {
      log.error("Error tracking event: ", error)
      throw error
    }
  }

  public async sendEvents(): Promise<void> {
    if (this.eventBuffer.length === 0) {
      // log.debug("No events to send")
      return
    }

    const eventsToSend = [...this.eventBuffer]

    try {
      const response = await fetch("/api/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: eventsToSend }),
      })

      if (response.ok) {
        this.processedEvents.clear()
        this.resetEvents()
      } else {
        throw new Error("Failed to send events")
      }
    } catch (error) {
      log.error("Error sending events:", error)
      this.eventBuffer.push(...eventsToSend)
    }
  }

  private autoFlushEvents(): void {
    if (this.eventFlushInterval) return

    const flushInterval =
      Number(process.env.NEXT_PUBLIC_EVENT_FLUSH_INTERVAL) ?? 5000

    this.eventFlushInterval = setInterval(() => {
      // log.debug("Auto-flushing events...")
      this.sendEvents()
    }, flushInterval)
  }
}

export const eventTracker = new EventTracker()
